import React, { useEffect, useState } from 'react'
import AsyncSelect from 'react-select/async'
import SearchableSelect from 'components/atoms/searchable-select'
import { Button, Card, CardBody, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import InboundJourneyApiV2 from 'api/v2/admins/inbound-journey-api-v2'
import SellersApiV2 from 'api/v2/admins/sellers-api-v2'
import { getPhase3ErrorMessage } from '../helpers/phase3-inbound-journey-helper'
import CsvUploadCard from 'components/organisms/csv-upload-card'
import { useAuth } from 'providers/auth-provider'
import { phase3July20FixedCategoryForUser } from '../constants'
import { ENABLE_CONSIGNMENT_NOTES } from 'constants/consignment-notes-config'

const EMPTY_PRODUCT = {
  supplier_name         : '',
  group_id              : '',
  inventory_type        : '',
  designer_id           : '',
  category_id           : '',
  condition_id          : '',
  name                  : '',
  p_size                : '',
  insole_size           : '',
  quantity              : 1,
  comes_with            : '',
  defects               : '',
  internal_note         : '',
  product_curator_pic   : '',
  receiver_pic          : '',
  stockist_pic          : '',
  internal_search_keyword : '',
  photo_folder_link     : '',
  item_location_id      : '',
  box_location_id       : '',
  inclusions_location_id: '',
  front_view            : null,
  right_side_view       : null,
  rear_view             : null,
  left_side_view        : null,
  top_view              : null,
  bottom_view           : null,
  comes_with_image      : null,
  stamp_image           : null,
  photo_details         : []
}

const EMPTY_FORM = {
  user_id             : '',
  consignor_detail_id : '',
  supplier_name       : '',
  consignment_type    : 'dropoff',
  consignment_method  : 'basic',
  notes               : '',
  products            : [ { ...EMPTY_PRODUCT } ]
}

const FALLBACK_OPTIONS = {
  consignment_types   : [
    { id: 'pickup', value: 'pickup', name: 'Pickup' },
    { id: 'dropoff', value: 'dropoff', name: 'Dropoff' },
    { id: 'online', value: 'online', name: 'Online' }
  ],
  consignment_methods : [
    { id: 'basic', value: 'basic', name: 'Basic' },
    { id: 'direct_sell', value: 'direct_sell', name: 'Direct Sell' },
    { id: 'trade', value: 'trade', name: 'Trade' }
  ],
  groups              : [],
  designers           : [],
  categories          : [],
  conditions          : [],
  inventory_types     : [
    { id: 'ownstock_trade', value: 'ownstock_trade', name: 'Ownstock / Trade' },
    { id: 'ownstock_direct_sell', value: 'ownstock_direct_sell', name: 'Ownstock / Direct Sell' },
    { id: 'ownstock_stock_issue', value: 'ownstock_stock_issue', name: 'Ownstock / Stock Issue' },
    { id: 'ownstock_buy_stock', value: 'ownstock_buy_stock', name: 'Ownstock / Buy Stock' },
    { id: 'multiple_ownstock_buy_stock', value: 'multiple_ownstock_buy_stock', name: 'Multiple Ownstock / Buy Stock' },
    { id: 'consign', value: 'consign', name: 'Consign' }
  ],
  item_locations      : [],
  box_locations       : [],
  inclusions_locations: []
}


const CREATE_CONSIGN_SAMPLE_CSV = [
  'group_code,designer_code,category_id,condition_name,product_name,comes_with,defects,product_curator_pic,receiver_pic,shoes_size,insole_size',
  'BKR,HR,1,New,Hermes Birkin 25 Gold Togo,"Box, Dustbag","Minor hairline on hardware",Corry,Ria,,',
  'BKR,CH,1,Excellent,Chanel Classic Flap Black Caviar,"Dustbag, Card","Light corner rubbing",Corry,Ria,,'
].join('\n')
const CREATE_CONSIGN_SAMPLE_CSV_URL = `data:text/csv;charset=utf-8,${encodeURIComponent(CREATE_CONSIGN_SAMPLE_CSV)}`
const PRODUCT_CURATOR_PIC_OPTIONS = [ 'MITA', 'DARA', 'SARAH', 'AGNES', 'VIKA', 'KARLA', 'JANE', 'LISA', 'LUNA', 'ELLA', 'KARIN', 'REGINA' ]

const PRODUCT_CARD_THEMES = [
  { background: '#fff8f2', border: '#f6b26b', badge: '#f6b26b', text: '#5f3a12' },
  { background: '#f3f8ff', border: '#6fa8dc', badge: '#6fa8dc', text: '#163a5f' },
  { background: '#f4fff7', border: '#63c384', badge: '#63c384', text: '#1f5a34' },
  { background: '#fff5fb', border: '#e98bb8', badge: '#e98bb8', text: '#61314b' },
  { background: '#f8f5ff', border: '#9b7bd3', badge: '#9b7bd3', text: '#3d2b62' }
]

const productCardTheme = (index) => PRODUCT_CARD_THEMES[index % PRODUCT_CARD_THEMES.length]

const productCardStyle = (index) => {
  const theme = productCardTheme(index)

  return {
    backgroundColor: theme.background,
    borderColor    : theme.border,
    borderWidth    : '2px'
  }
}

const productBadgeStyle = (index) => {
  const theme = productCardTheme(index)

  return {
    backgroundColor: theme.badge,
    color          : '#ffffff',
    fontSize       : '13px',
    padding        : '7px 12px'
  }
}

const safeString = (value) => value == null ? '' : value.toString()

const normalizeCsvKey = (value = '') => safeString(value)
  .replace(/^\uFEFF/, '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')

const normalizeLookupValue = (value = '') => safeString(value)
  .trim()
  .toLowerCase()
  .replace(/\s+/g, ' ')

const csvValue = (row, keys, fallback = '') => {
  for (const key of keys) {
    const normalizedKey = normalizeCsvKey(key)
    const value = row[normalizedKey]

    if (value != null && value.toString().trim() !== '') return value.toString().trim()
  }

  return fallback
}

const parseCsvLine = (line = '') => {
  const result = []
  let current = ''
  let insideQuote = false

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    const nextChar = line[index + 1]

    if (char === '"' && insideQuote && nextChar === '"') {
      current += '"'
      index += 1
    } else if (char === '"') {
      insideQuote = !insideQuote
    } else if (char === ',' && !insideQuote) {
      result.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }

  result.push(current.trim())

  return result
}

const parseProductsCsv = (text = '') => {
  const lines = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)

  if (lines.length <= 1) return []

  const headers = parseCsvLine(lines[0]).map(normalizeCsvKey)

  return lines.slice(1).map((line) => {
    const values = parseCsvLine(line)
    return headers.reduce((acc, header, index) => ({ ...acc, [header]: values[index] || '' }), {})
  })
}

const findOptionByCsvValue = (items = [], value = '') => {
  const lookupValue = normalizeLookupValue(value)
  if (!lookupValue) return null

  return (items || []).find((option) => {
    const values = [ option.id, option.value, option.code, option.name, option.label ].map(normalizeLookupValue)

    return values.includes(lookupValue)
  }) || null
}

const optionIdFromCsv = (items = [], value = '') => {
  const match = findOptionByCsvValue(items, value)
  if (match) return optionValue(match) || ''

  const rawValue = safeString(value).trim()
  return rawValue.match(/^\d+$/) ? rawValue : ''
}

const inventoryTypeFromCsv = (inventoryTypes = [], group = {}, value = '') => {
  const groupOptions = inventoryTypeOptionsForGroup(inventoryTypes, group)
  const match = findOptionByCsvValue(groupOptions, value)

  return inventoryTypeValue(match || {}) || defaultInventoryTypeForGroup(inventoryTypes, group)
}

const buildProductFromCsvRow = ({ row, options, selectedConsignor }) => {
  const groupId = optionIdFromCsv(options.groups, csvValue(row, [ 'group_id', 'group_code', 'group', 'supply_method' ]))
  const selectedGroup = selectedOptionById(options.groups, groupId)

  return {
    ...EMPTY_PRODUCT,
    supplier_name           : selectedConsignor?.label || csvValue(row, [ 'supplier_name', 'supplier', 'consignor_name' ]),
    group_id                : groupId,
    inventory_type          : inventoryTypeFromCsv(options.inventory_types, selectedGroup, csvValue(row, [ 'inventory_type', 'inventory_type_code' ])),
    designer_id             : optionIdFromCsv(options.designers, csvValue(row, [ 'designer_id', 'designer_code', 'designer', 'brand' ])),
    category_id             : optionIdFromCsv(options.categories, csvValue(row, [ 'category_id', 'category_code', 'category' ])),
    condition_id            : optionIdFromCsv(options.conditions, csvValue(row, [ 'condition_id', 'condition_name', 'condition' ])),
    name                    : csvValue(row, [ 'name', 'product_name' ]),
    p_size                  : csvValue(row, [ 'shoes_size', 'shoe_size', 'product_size', 'p_size' ]),
    insole_size             : csvValue(row, [ 'insole_size' ]),
    quantity                : 1,
    comes_with              : csvValue(row, [ 'comes_with', 'inclusions', 'kelengkapan' ]),
    defects                 : csvValue(row, [ 'defects', 'condition_note', 'dosa' ]),
    internal_note           : '',
    product_curator_pic     : csvValue(row, [ 'product_curator_pic', 'pic_product_curator', 'curator_pic' ]),
    receiver_pic            : csvValue(row, [ 'receiver_pic', 'pic_penerima', 'received_by' ]),
    stockist_pic            : '',
    internal_search_keyword : '',
    photo_folder_link       : csvValue(row, [ 'photo_folder_link', 'google_drive_photo_folder_link', 'photo_link', 'drive_link' ]),
    item_location_id        : optionIdFromCsv(options.item_locations, csvValue(row, [ 'item_location_id', 'item_location', 'lokasi_produk' ])),
    box_location_id         : optionIdFromCsv(options.box_locations, csvValue(row, [ 'box_location_id', 'box_location', 'lokasi_box' ])),
    inclusions_location_id  : optionIdFromCsv(options.inclusions_locations, csvValue(row, [ 'inclusions_location_id', 'inclusions_location', 'lokasi_kelengkapan' ]))
  }
}

const optionValue = (option = {}) => option == null ? '' : (option.value == null ? option.id : option.value)

const optionLabel = (option) => {
  if (option.code) return `${option.name} (${option.code})`

  return option.name || option.label || option.id
}

const selectedOptionById = (items, id) => (items || []).find((option) => String(optionValue(option)) === String(id))

const inventoryTypeValue = (option) => optionValue(option)

const inventoryTypeOptionsForGroup = (inventoryTypes = [], group = {}) => {
  const groupCode = (group?.code || '').toString().toUpperCase()
  const allowedByGroup = {
    BKR: [ 'consign' ],
    BKT: [ 'multiple_ownstock_buy_stock' ],
    BKA: [ 'ownstock_trade', 'ownstock_direct_sell', 'ownstock_stock_issue', 'ownstock_buy_stock' ]
  }
  const allowedValues = allowedByGroup[groupCode]

  if (!allowedValues) return []

  return inventoryTypes.filter((option) => allowedValues.includes(inventoryTypeValue(option)))
}

const defaultInventoryTypeForGroup = (inventoryTypes = [], group = {}) => {
  const groupOptions = inventoryTypeOptionsForGroup(inventoryTypes, group)

  return inventoryTypeValue(groupOptions[0] || {}) || ''
}

const formatConsignorOption = (seller = {}) => ({
  value   : seller.id,
  label   : seller.name || seller.email || seller.code || `User #${seller.id}`,
  code    : seller.code,
  email   : seller.email,
  type    : seller.seller_type,
  details : seller.details || []
})

const formatConsignorOptionLabel = ({ code, label, email }) => (
  <>
    <span className='mr-3'>{ code || '-' }</span>
    <span className='mr-3'>{ label || '-' }</span>
    <span className='text-muted'>{ email || '-' }</span>
  </>
)

const loadConsignorOptions = async (keyword = '') => {
  const response = await SellersApiV2.get({
    additionalFilters: {
      code: keyword
    }
  })
  const sellers = response.data?.sellers || []

  return sellers.map(formatConsignorOption)
}

const normalizedConsignorName = (value = '') => safeString(value).trim().toLowerCase().replace(/\s+/g, ' ')

const consignorDetailsForOption = (option) => Array.isArray(option?.details) ? option.details : []

const consignorDetailName = (detail = {}) => detail.name || ''

const findConsignorDetailByName = (option, name) => {
  const normalizedName = normalizedConsignorName(name)
  if (!normalizedName) return null

  return consignorDetailsForOption(option).find((detail) => normalizedConsignorName(consignorDetailName(detail)) === normalizedName) || null
}

const defaultConsignorDetailForOption = (option) => {
  const details = consignorDetailsForOption(option)

  return details.find((detail) => detail.primary) || details[0] || null
}

const mergeCreatedConsignorDetail = (option, createdDetail) => {
  if (!option || !createdDetail?.id) return option

  const details = consignorDetailsForOption(option)
  const existing = details.find((detail) => String(detail.id) === String(createdDetail.id))
  const nextDetail = {
    ...existing,
    id      : createdDetail.id,
    name    : createdDetail.formatted_name || createdDetail.name || existing?.name,
    primary : existing?.primary || false
  }

  return {
    ...option,
    details: [ ...details.filter((detail) => String(detail.id) !== String(createdDetail.id)), nextDetail ]
  }
}

const searchableSelectOptions = (items = []) => (items || [])
  .filter((option) => optionValue(option) !== undefined && optionValue(option) !== null && optionValue(option) !== '')
  .map((option) => ({
    value : String(optionValue(option)),
    label : optionLabel(option),
    code  : option.code
  }))

const requiredProductFields = [
  { key: 'group_id', label: 'Group' },
  { key: 'designer_id', label: 'Designer' },
  { key: 'category_id', label: 'Category' },
  { key: 'condition_id', label: 'Condition' },
  { key: 'name', label: 'Product Name' },
  { key: 'comes_with', label: 'Comes With' },
  { key: 'defects', label: 'Defects' },
  { key: 'product_curator_pic', label: 'PIC Product Curator' },
  { key: 'receiver_pic', label: 'PIC Penerima' }
]

const isShoesCategory = (product = {}) => Number(product.category_id) === 2

const validShoesMeasurement = (value, max = Infinity) => {
  const normalized = safeString(value).trim().replace(',', '.')
  const number = Number(normalized)

  return /^\d+(?:\.\d+)?$/.test(normalized) && Number.isFinite(number) && number > 0 && number <= max
}

const missingRequiredProductFields = (product = {}) => [
  ...requiredProductFields
    .filter((field) => safeString(product[field.key]).trim() === '')
    .map((field) => field.label),
  ...(isShoesCategory(product) && !validShoesMeasurement(product.p_size, 50) ? [ 'Shoes Size (angka > 0 hingga 50)' ] : []),
  ...(isShoesCategory(product) && safeString(product.insole_size).trim() !== '' && !validShoesMeasurement(product.insole_size) ? [ 'Insole Size (jika diisi, angka > 0)' ] : [])
]

const CREATE_CONSIGN_INTERNAL_CATEGORY_GROUP_CODES = [ 'BKR', 'BKA', 'BKO', 'BKC', 'BRV', 'BKV', 'PO' ]

const createConsignInternalCategory = (product = {}, options = {}) => {
  const group = selectedOptionById(options.groups, product.group_id)
  const designer = selectedOptionById(options.designers, product.designer_id)
  const category = selectedOptionById(options.categories, product.category_id)
  const groupCode = safeString(group?.code).trim().toUpperCase()
  const designerCode = safeString(designer?.code).trim().toUpperCase()
  const categoryCode = safeString(category?.code).trim().toUpperCase()
  const designerName = normalizeLookupValue(designer?.name)
  const categoryName = normalizeLookupValue(category?.name)

  if ([ 'BRV', 'BKV' ].includes(groupCode)) return 'C'
  if (!CREATE_CONSIGN_INTERNAL_CATEGORY_GROUP_CODES.includes(groupCode)) return ''

  const isWatch = categoryCode === '04' || String(optionValue(category)) === '4' || [ 'watch', 'watches' ].includes(categoryName)
  if (isWatch) return 'W'

  const isHermes = designerCode === 'HR' || String(optionValue(designer)) === '1' || designerName === 'hermes'
  const isBag = categoryCode === '01' || String(optionValue(category)) === '1' || [ 'bag', 'bags' ].includes(categoryName)
  if (isHermes && isBag) return 'H'

  return 'C'
}

const Phase3CreateIntakePage = ({ pageUtils, apiNamespace = '' }) => {
  const { user } = useAuth()
  const fixedCreateConsignCategory = phase3July20FixedCategoryForUser(user)
  const [ form, setForm ] = useState(EMPTY_FORM)
  const [ options, setOptions ] = useState(FALLBACK_OPTIONS)
  const [ selectedConsignor, setSelectedConsignor ] = useState(null)
  const [ defaultConsignorOptions, setDefaultConsignorOptions ] = useState([])
  const [ isSubmitting, setIsSubmitting ] = useState(false)
  const [ isCsvUploading, setIsCsvUploading ] = useState(false)
  const [ result, setResult ] = useState(null)
  const [ createdProductReviews, setCreatedProductReviews ] = useState([])
  const [ lastCreatedConsignorId, setLastCreatedConsignorId ] = useState(null)
  const [ expandedProducts, setExpandedProducts ] = useState({ 0: true })
  const [ isCsvGuideExpanded, setIsCsvGuideExpanded ] = useState(false)

  useEffect(() => {
    fetchOptions()
    fetchDefaultConsignors()
  }, [ apiNamespace ]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOptions = async () => {
    try {
      const response = await InboundJourneyApiV2.getConsignmentOptions({ apiNamespace })
      setOptions({ ...FALLBACK_OPTIONS, ...(response.data?.data || {}) })
    } catch (error) {
      setOptions(FALLBACK_OPTIONS)
    }
  }

  const fetchDefaultConsignors = async () => {
    try {
      const consignors = await loadConsignorOptions('')
      setDefaultConsignorOptions(consignors)
    } catch (error) {
      setDefaultConsignorOptions([])
    }
  }

  const renderOptions = (items, placeholder) => (
    <>
      <option value=''>{ placeholder }</option>
      { (items || []).map((option) => (
        <option key={ `${option.id}-${optionValue(option)}` } value={ optionValue(option) }>
          { optionLabel(option) }
        </option>
      )) }
    </>
  )

  const renderSearchableProductSelect = ({ label, items, value, onChange }) => (
    <FormGroup>
      <Label>{ label }</Label>
      <SearchableSelect
        options={ searchableSelectOptions(items) }
        value={ value }
        onChange={ onChange }
        placeholder={ `Search ${label}` }
      />
    </FormGroup>
  )

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

  const handleChangeConsignor = (option) => {
    const defaultDetail = defaultConsignorDetailForOption(option)
    const supplierName = consignorDetailName(defaultDetail) || option?.label || ''

    setSelectedConsignor(option)
    setForm((prev) => ({
      ...prev,
      user_id: option?.value || '',
      consignor_detail_id: defaultDetail?.id ? String(defaultDetail.id) : '',
      supplier_name: supplierName,
      products: prev.products.map((product) => ({ ...product, supplier_name: supplierName }))
    }))
  }

  const handleChangeConsignorDetail = (detailId) => {
    const selectedDetail = consignorDetailsForOption(selectedConsignor).find((detail) => String(detail.id) === String(detailId))
    const supplierName = consignorDetailName(selectedDetail) || ''

    setForm((prev) => ({
      ...prev,
      consignor_detail_id: selectedDetail?.id ? String(selectedDetail.id) : '',
      supplier_name: supplierName,
      products: prev.products.map((product) => ({ ...product, supplier_name: supplierName }))
    }))
  }

  const handleChangeSupplierName = (supplierName) => {
    const matchingDetail = findConsignorDetailByName(selectedConsignor, supplierName)

    setForm((prev) => ({
      ...prev,
      consignor_detail_id: matchingDetail?.id ? String(matchingDetail.id) : '',
      supplier_name: supplierName,
      products: prev.products.map((product) => ({ ...product, supplier_name: supplierName }))
    }))
  }

  const setProductField = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.map((product, productIndex) => productIndex === index ? { ...product, [key]: value } : product)
    }))
  }

  const setProductGroup = (index, groupId) => {
    const selectedGroup = selectedOptionById(options.groups, groupId)
    const inventoryType = defaultInventoryTypeForGroup(options.inventory_types, selectedGroup)

    setForm((prev) => ({
      ...prev,
      products: prev.products.map((product, productIndex) => (
        productIndex === index ? { ...product, group_id: groupId, inventory_type: inventoryType } : product
      ))
    }))
  }

  const addProduct = () => {
    setForm((prev) => {
      const nextIndex = prev.products.length
      setExpandedProducts((expanded) => ({ ...expanded, [nextIndex]: true }))

      return { ...prev, products: [ ...prev.products, { ...EMPTY_PRODUCT, supplier_name: selectedConsignor?.label || prev.products[0]?.supplier_name || '' } ] }
    })
  }

  const removeProduct = (index) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.length <= 1 ? prev.products : prev.products.filter((_, productIndex) => productIndex !== index)
    }))

    setExpandedProducts((expanded) => {
      const nextExpanded = {}

      Object.keys(expanded).forEach((key) => {
        const currentIndex = Number(key)
        if (currentIndex < index) nextExpanded[currentIndex] = expanded[key]
        if (currentIndex > index) nextExpanded[currentIndex - 1] = expanded[key]
      })

      return nextExpanded
    })
  }

  const toggleProductExpanded = (index) => {
    setExpandedProducts((expanded) => ({ ...expanded, [index]: !expanded[index] }))
  }

  const isProductExpanded = (index) => !!expandedProducts[index]

  const handleUploadProductsCsv = async (file) => {
    if (!file || file.size === 0) {
      pageUtils?.setAlertMsg?.('Pilih file CSV terlebih dahulu')
      return
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      pageUtils?.setAlertMsg?.('Format upload Create Consign saat ini hanya CSV. Silakan download template CSV lalu upload ulang.')
      return
    }

    setIsCsvUploading(true)

    try {
      const text = await file.text()
      const rows = parseProductsCsv(text)

      if (rows.length === 0) {
        pageUtils?.setAlertMsg?.('CSV tidak memiliki row product. Pastikan header dan data sudah terisi.')
        return
      }

      const csvProducts = rows.map((row) => buildProductFromCsvRow({ row, options, selectedConsignor }))

      setForm((prev) => ({
        ...prev,
        products: csvProducts.length > 0 ? csvProducts : prev.products
      }))
      setExpandedProducts({})

      pageUtils?.setAlertMsg?.(`${csvProducts.length} product berhasil dimasukkan dari CSV. Detail product disembunyikan dulu; klik Expand Detail untuk review sebelum Create Consign.`)
    } catch (error) {
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg({ messages: { base: error.message } }) : alert(error.message)
    } finally {
      setIsCsvUploading(false)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!form.user_id) {
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg({ messages: { base: 'Pilih Consignor/User terlebih dahulu.' } }) : alert('Pilih Consignor/User terlebih dahulu.')
      return
    }

    if (!safeString(form.supplier_name).trim()) {
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg({ messages: { base: 'Pilih Consignor Detail atau isi Supplier Name / Seller Name.' } }) : alert('Pilih Consignor Detail atau isi Supplier Name / Seller Name.')
      return
    }

    const invalidProducts = form.products
      .map((product, index) => ({ index, missingFields: missingRequiredProductFields(product) }))
      .filter((item) => item.missingFields.length > 0)

    if (invalidProducts.length > 0) {
      setExpandedProducts((expanded) => invalidProducts.reduce((acc, item) => ({ ...acc, [item.index]: true }), expanded))
      const message = invalidProducts
        .map((item) => `Product #${item.index + 1}: ${item.missingFields.join(', ')}`)
        .join(' | ')

      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg({ messages: { base: `Periksa kelengkapan atau format field: ${message}` } }) : alert(message)
      return
    }

    // if (fixedCreateConsignCategory) {
    //   const blockedProducts = form.products
    //     .map((product, index) => ({ index, category: createConsignInternalCategory(product, options) }))
    //     .filter((item) => item.category !== fixedCreateConsignCategory)

    //   if (blockedProducts.length > 0) {
    //     setExpandedProducts((expanded) => blockedProducts.reduce((acc, item) => ({ ...acc, [item.index]: true }), expanded))
    //     const message = blockedProducts
    //       .map((item) => `Product #${item.index + 1}: ${item.category || 'Internal Category belum dapat ditentukan'}`)
    //       .join(' | ')

    //     pageUtils?.setApiErrorMsg
    //       ? pageUtils.setApiErrorMsg({ messages: { base: `Akun ini hanya dapat Create Consign untuk Internal Category ${fixedCreateConsignCategory}. ${message}` } })
    //       : alert(`Akun ini hanya dapat Create Consign untuk Internal Category ${fixedCreateConsignCategory}. ${message}`)
    //     return
    //   }
    // }

    const sameConsignorAsPreviousCreate = createdProductReviews.length > 0 &&
      lastCreatedConsignorId &&
      String(form.user_id) === String(lastCreatedConsignorId)

    if (sameConsignorAsPreviousCreate) {
      const consignorLabel = form.supplier_name || selectedConsignor?.label || `User #${form.user_id}`
      const consignorEmail = selectedConsignor?.email ? ` - ${selectedConsignor.email}` : ''
      const confirmed = window.confirm(
        `Product sebelumnya sudah berhasil dibuat untuk consignor ${consignorLabel}${consignorEmail}.\n\n` +
        'Apakah yakin ingin membuat product berikutnya dengan consignor yang sama?\n\n' +
        'OK = lanjut create dengan consignor yang sama.\nCancel = kembali dan ganti consignor terlebih dahulu.'
      )

      if (!confirmed) {
        pageUtils?.setAlertMsg?.('Create Product dibatalkan. Silakan ganti Consignor jika produk berikutnya milik consignor yang berbeda.')
        return
      }
    }

    setIsSubmitting(true)
    setResult(null)

    const productAttributesList = form.products.map((product) => ({
      supplier_name          : product.supplier_name,
      group_id                : product.group_id,
      designer_id             : product.designer_id,
      category_id             : product.category_id,
      condition_id            : product.condition_id,
      inventory_type          : product.inventory_type,
      name                    : product.name,
      p_size                  : isShoesCategory(product) ? product.p_size : '',
      insole_size             : isShoesCategory(product) ? product.insole_size : '',
      quantity                : 1,
      comes_with              : product.comes_with,
      defects                 : product.defects,
      internal_note           : product.internal_note,
      product_curator_pic     : product.product_curator_pic,
      receiver_pic            : product.receiver_pic,
      stockist_pic            : product.stockist_pic,
      internal_search_keyword : product.internal_search_keyword,
      photo_folder_link       : product.photo_folder_link,
      item_location_id        : product.item_location_id,
      box_location_id         : product.box_location_id,
      inclusions_location_id  : product.inclusions_location_id,
      front_view              : product.front_view,
      right_side_view         : product.right_side_view,
      rear_view               : product.rear_view,
      left_side_view          : product.left_side_view,
      top_view                : product.top_view,
      bottom_view             : product.bottom_view,
      comes_with_image        : product.comes_with_image,
      stamp_image             : product.stamp_image,
      photo_details           : product.photo_details
    }))

    const consignment = {
      user_id                  : form.user_id,
      consignor_detail_id      : form.consignor_detail_id,
      supplier_name            : form.supplier_name,
      consignment_type         : form.consignment_type,
      consignment_method       : form.consignment_method,
      quantity                 : 1,
      notes                    : ENABLE_CONSIGNMENT_NOTES ? form.notes : '',
      product_attributes       : productAttributesList[0],
      product_attributes_list  : productAttributesList
    }

    try {
      const response = await InboundJourneyApiV2.createConsignmentWithProduct({ consignment, apiNamespace })
      const createdResult = response.data?.data || response.data
      const createdProducts = createdResult?.products || (createdResult?.product ? [ createdResult.product ] : [])
      const createdConsignorId = createdResult?.consignor?.id || form.user_id
      const createdConsignorName = createdResult?.consignor_detail?.name || createdResult?.consignor?.name || selectedConsignor?.label || '-'
      const createdConsignorEmail = createdResult?.consignor?.email || selectedConsignor?.email || '-'
      const createdReviews = createdProducts.map((product) => ({
        ...product,
        consignment_id  : createdResult?.id,
        consignor_id    : createdConsignorId,
        consignor_name  : createdConsignorName,
        consignor_email : createdConsignorEmail
      }))

      pageUtils?.setAlertMsg?.('Consignment + produk berhasil dibuat. Form produk sudah dikosongkan untuk produk berikutnya.')
      setCreatedProductReviews((previous) => [ ...previous, ...createdReviews ])
      setLastCreatedConsignorId(createdConsignorId)
      setResult(createdResult)
      if (createdResult?.consignor_detail?.id) {
        setSelectedConsignor((previous) => mergeCreatedConsignorDetail(previous, createdResult.consignor_detail))
      }
      setForm((previous) => ({
        ...previous,
        consignor_detail_id: createdResult?.consignor_detail?.id ? String(createdResult.consignor_detail.id) : previous.consignor_detail_id,
        supplier_name: createdConsignorName || previous.supplier_name,
        products: [ { ...EMPTY_PRODUCT, supplier_name: createdConsignorName || previous.supplier_name || '' } ]
      }))
      setExpandedProducts({ 0: true })
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const resultProducts = result?.products || (result?.product ? [ result.product ] : [])
  const resultProductIds = resultProducts.map((product) => product.id).filter(Boolean).join(',')

  return (
    <>
      <TitlePage mainTitle='Inbound Journey' subTitle='Create Consign' />
      <datalist id='phase3-product-curator-pic-options'>
        { PRODUCT_CURATOR_PIC_OPTIONS.map((name) => <option key={ name } value={ name } />) }
      </datalist>

      <Card className='mb-3'>
        <CardBody>
          <h4 className='mb-2'>Alur Create Consign</h4>
          { fixedCreateConsignCategory && (
            <div className='alert alert-info py-2'>
              Product access akun ini dibatasi ke Internal Category <strong>{ fixedCreateConsignCategory }</strong>. Product di luar kategori tersebut akan ditolak saat Create Consign.
            </div>
          ) }
          <div className='text-muted'>
            <div><strong>1 consign dapat menangani beberapa produk.</strong> Gunakan Tambah Produk Lain jika consignor yang sama mengirim lebih dari satu item.</div>
            <div><strong>Inventory Batch dibuat setelah submit.</strong> Gunakan baris Inventory Batch untuk serah-terima fisik ke Inbound / Studio Team.</div>
            <div><strong>Bulk-live dilakukan dari alur batch.</strong> Setelah produk siap untuk live, Inbound dapat melakukan bulk-live pada consignment agar produk yang siap dapat live bersamaan.</div>
          </div>
        </CardBody>
      </Card>

      { createdProductReviews.length > 0 && (
        <Card className='mb-3'>
          <CardBody>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <h4 className='mb-0'>Produk yang Dibuat pada Sesi Ini</h4>
              <span className='badge badge-success'>{ createdProductReviews.length } produk dibuat</span>
            </div>
            <div className='text-muted mb-3'>
              Product di bawah sudah berhasil dibuat dan tidak akan ikut dikirim ulang saat Create Product berikutnya.
            </div>
            <div className='table-responsive'>
              <table className='table table-bordered table-sm mb-0'>
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Product Name</th>
                    <th>Spec</th>
                    <th>Consignor</th>
                    <th>Inventory Detail</th>
                  </tr>
                </thead>
                <tbody>
                  { createdProductReviews.map((product) => (
                    <tr key={ `${product.consignment_id}-${product.id}` }>
                      <td>{ product.sku || '-' }</td>
                      <td>{ product.name || '-' }</td>
                      <td>
                        { product.designer_name || '-' } / { product.category_name || '-' } / { product.condition_name || '-' }
                      </td>
                      <td>
                        <div>{ product.consignor_name || '-' }</div>
                        <small className='text-muted'>{ product.consignor_email || '-' }</small>
                      </td>
                      <td>
                        { product.inventory?.id ? (
                          <a
                            href={ `/app/warehouse-team/inventory/${product.inventory.id}` }
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            Buka Detail Inventory
                          </a>
                        ) : '-' }
                      </td>
                    </tr>
                  )) }
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      ) }

      <Card>
        <CardBody>
          <Form onSubmit={ handleSubmit }>
            <h4>Consignment</h4>
            <Row form>
              <Col md='4'>
                <FormGroup>
                  <Label>Consignor</Label>
                  <AsyncSelect
                    className='react-select success'
                    classNamePrefix='react-select'
                    cacheOptions
                    defaultOptions={ defaultConsignorOptions }
                    value={ selectedConsignor }
                    loadOptions={ loadConsignorOptions }
                    onChange={ handleChangeConsignor }
                    formatOptionLabel={ formatConsignorOptionLabel }
                    placeholder='Type seller email / name / ID'
                    isClearable
                  />
                  { form.user_id && (
                    <small className='text-muted'>
                      User ID: { form.user_id }
                    </small>
                  ) }
                </FormGroup>
              </Col>
              <Col md='4'>
                <FormGroup>
                  <Label>Consignor Detail</Label>
                  <Input
                    type='select'
                    value={ form.consignor_detail_id }
                    disabled={ !form.user_id }
                    onChange={ (e) => handleChangeConsignorDetail(e.target.value) }
                  >
                    <option value=''>Buat detail baru dari Supplier Name</option>
                    { consignorDetailsForOption(selectedConsignor).map((detail) => (
                      <option key={ detail.id } value={ detail.id }>
                        { detail.primary ? '[Primary] ' : '' }{ consignorDetailName(detail) || `Detail #${detail.id}` } — Detail #{ detail.id }
                      </option>
                    )) }
                  </Input>
                  { form.user_id && form.supplier_name && !form.consignor_detail_id && (
                    <small className='text-warning'>
                      Akan membuat User Detail baru saat submit untuk supplier: { form.supplier_name }
                    </small>
                  ) }
                </FormGroup>
              </Col>
              <Col md='4'>
                <FormGroup>
                  <Label>Supplier Name / Seller Name</Label>
                  <Input
                    value={ form.supplier_name }
                    disabled={ !form.user_id }
                    placeholder='Contoh: Carson Seller'
                    onChange={ (e) => handleChangeSupplierName(e.target.value) }
                  />
                  <small className='text-muted'>
                    Pilih detail yang sudah ada atau ketik nama baru. Nama baru akan dibuat sebagai User Detail non-primary saat submit; data bank/alamat tidak disalin otomatis.
                  </small>
                </FormGroup>
              </Col>
            </Row>
            <Row form>
              <Col md='6'>
                <FormGroup>
                  <Label>Consignment Type</Label>
                  <Input type='select' value={ form.consignment_type } onChange={ (e) => setField('consignment_type', e.target.value) }>
                    { renderOptions(options.consignment_types, 'Select Consignment Type') }
                  </Input>
                </FormGroup>
              </Col>
              <Col md='6'>
                <FormGroup>
                  <Label>Consignment Method</Label>
                  <Input type='select' value={ form.consignment_method } onChange={ (e) => setField('consignment_method', e.target.value) }>
                    { renderOptions(options.consignment_methods, 'Select Consignment Method') }
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            { ENABLE_CONSIGNMENT_NOTES && (
              <FormGroup>
                <Label>Notes</Label>
                <Input type='textarea' value={ form.notes } onChange={ (e) => setField('notes', e.target.value) } />
              </FormGroup>
            ) }

            <div className='mt-4 mb-4'>
              <Button
                type='button'
                color='primary'
                outline
                size='sm'
                onClick={ () => setIsCsvGuideExpanded((expanded) => !expanded) }
              >
                Panduan Import CSV
              </Button>

              { isCsvGuideExpanded && (
                <div className='border rounded p-3 mt-3'>
                  <div className='d-flex justify-content-end mb-3'>
                    <a
                      className='btn btn-outline-primary btn-sm'
                      href={ CREATE_CONSIGN_SAMPLE_CSV_URL }
                      download='create-consign-bulk-product-template.csv'
                    >
                      Download Template CSV
                    </a>
                  </div>

                  <CsvUploadCard
                    label='Import Produk dari CSV'
                    onFileUpload={ handleUploadProductsCsv }
                    isUploading={ isCsvUploading }
                  />
                </div>
              ) }
            </div>

            <hr />
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h4 className='mb-0'>Products</h4>
              <Button type='button' color='secondary' size='sm' onClick={ addProduct }>
                Add Another Product
              </Button>
            </div>

            { form.products.map((product, index) => (
              <Card key={ index } className='mb-4 border' style={ productCardStyle(index) }>
                <CardBody>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <div>
                      <span className='badge badge-pill mr-2' style={ productBadgeStyle(index) }>Product #{ index + 1 }</span>
                      <span className='font-weight-bold text-dark'>{ product.name || 'Product detail belum diisi' }</span>
                      { product.group_id && (
                        <span className='text-muted ml-2'>Group: { selectedOptionById(options.groups, product.group_id)?.code || selectedOptionById(options.groups, product.group_id)?.name || product.group_id }</span>
                      ) }
                    </div>
                    <div className='d-flex align-items-center' style={ { gap: 8 } }>
                      <Button type='button' color='secondary' outline size='sm' onClick={ () => toggleProductExpanded(index) }>
                        { isProductExpanded(index) ? 'Sembunyikan Detail' : 'Tampilkan Detail' }
                      </Button>
                      <Button type='button' color='danger' size='sm' disabled={ form.products.length <= 1 } onClick={ () => removeProduct(index) }>
                        Hapus
                      </Button>
                    </div>
                  </div>

                  { isProductExpanded(index) && (
                    <>
                  <Row form>
                    <Col md='3'>
                      { renderSearchableProductSelect({ label: 'Group', items: options.groups, value: product.group_id, onChange: (value) => setProductGroup(index, value) }) }
                    </Col>
                    <Col md='3'>
                      { renderSearchableProductSelect({ label: 'Designer', items: options.designers, value: product.designer_id, onChange: (value) => setProductField(index, 'designer_id', value) }) }
                    </Col>
                    <Col md='3'>
                      { renderSearchableProductSelect({ label: 'Category', items: options.categories, value: product.category_id, onChange: (value) => setProductField(index, 'category_id', value) }) }
                    </Col>
                    <Col md='3'>
                      { renderSearchableProductSelect({ label: 'Condition', items: options.conditions, value: product.condition_id, onChange: (value) => setProductField(index, 'condition_id', value) }) }
                    </Col>
                  </Row>

                  { product.group_id && inventoryTypeOptionsForGroup(options.inventory_types, selectedOptionById(options.groups, product.group_id)).length > 0 && (
                    <Row form>
                      <Col md='4'>
                        <FormGroup>
                          <Label>Inventory Type</Label>
                          <Input type='select' value={ product.inventory_type } onChange={ (e) => setProductField(index, 'inventory_type', e.target.value) }>
                            { renderOptions(inventoryTypeOptionsForGroup(options.inventory_types, selectedOptionById(options.groups, product.group_id)), 'Select Inventory Type') }
                          </Input>
                          <small className='text-muted'>BKA can choose Ownstock type. BKT is Multiple Ownstock / Buy Stock. BKR is Consign.</small>
                        </FormGroup>
                      </Col>
                    </Row>
                  ) }

                  <FormGroup>
                    <Label>Product Name</Label>
                    <Input value={ product.name } onChange={ (e) => setProductField(index, 'name', e.target.value) } />
                    <small className='text-muted'>Quantity otomatis 1 dari Create Consign. Kalau ada perubahan qty, lanjutkan dari proses Inventory / Studio.</small>
                  </FormGroup>

                  { isShoesCategory(product) && (
                    <Row form>
                      <Col md='6'>
                        <FormGroup>
                          <Label>Shoes Size <span className='text-danger'>*</span></Label>
                          <Input type='text' inputMode='decimal' value={ product.p_size } onChange={ (e) => setProductField(index, 'p_size', e.target.value) } />
                          <small className='text-muted'>Angka lebih dari 0 sampai 50, tanpa prefix EU / US / UK / AUS. Desimal boleh memakai titik atau koma.</small>
                        </FormGroup>
                      </Col>
                      <Col md='6'>
                        <FormGroup>
                          <Label>Insole Size</Label>
                          <Input type='text' inputMode='decimal' value={ product.insole_size } onChange={ (e) => setProductField(index, 'insole_size', e.target.value) } />
                          <small className='text-muted'>Insole Size opsional, akan diisi oleh tim Studio.</small>
                        </FormGroup>
                      </Col>
                    </Row>
                  ) }

                  <Row form>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Comes With</Label>
                        <Input type='textarea' value={ product.comes_with } onChange={ (e) => setProductField(index, 'comes_with', e.target.value) } />
                      </FormGroup>
                    </Col>
                    <Col md='8'>
                      <FormGroup>
                        <Label>Defects</Label>
                        <Input type='textarea' value={ product.defects } onChange={ (e) => setProductField(index, 'defects', e.target.value) } />
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>Catatan Internal</Label>
                    <Input
                      type='textarea'
                      rows='3'
                      value={ product.internal_note || '' }
                      onChange={ (e) => setProductField(index, 'internal_note', e.target.value) }
                    />
                    <small className='text-muted'>Catatan khusus Product #{ index + 1 }, tersimpan sebagai Internal Note produk.</small>
                  </FormGroup>

                  <Row form>
                    <Col md='6'>
                      <FormGroup>
                        <Label>PIC Product Curator <span className='text-danger'>*</span></Label>
                        <Input
                          list='phase3-product-curator-pic-options'
                          value={ product.product_curator_pic }
                          placeholder='MITA, DARA, SARAH, AGNES, VIKA, REGINA, atau Others...'
                          onChange={ (e) => setProductField(index, 'product_curator_pic', e.target.value) }
                        />
                      </FormGroup>
                    </Col>
                    <Col md='6'>
                      <FormGroup>
                        <Label>PIC Penerima <span className='text-danger'>*</span></Label>
                        <Input value={ product.receiver_pic } onChange={ (e) => setProductField(index, 'receiver_pic', e.target.value) } />
                      </FormGroup>
                    </Col>
                  </Row>

                    </>
                  ) }
                </CardBody>
              </Card>
            )) }

            <Button color='primary' disabled={ isSubmitting }>
              { isSubmitting ? 'Menyimpan...' : `Buat ${form.products.length} Produk` }
            </Button>
          </Form>

          { result && (
            <div className='alert alert-success mt-4'>
              <div className='font-weight-bold'>Create Consign berhasil diselesaikan</div>
              <div>Consignment ID: { result.id }</div>
              <div>
                Inventory Batch:{ ' ' }
                { result.inventory_batch?.id ? (
                  <a href={ `/app/inbound-journey/inventory-batches?batch_id=${result.inventory_batch.id}` }>
                    { result.inventory_batch_code || result.inventory_batch?.batch_code || '-' }
                  </a>
                ) : result.inventory_batch_code || result.inventory_batch?.batch_code || '-' }
              </div>
              <div>Products Created: { resultProducts.length }</div>
              <div className='mt-2'>Next: open Inventory Batch for handover to Inbound / Studio Team. After final check, use bulk-live so ready products in this consign can go live together.</div>
              { resultProducts.map((product) => (
                <div key={ product.id }>
                  Product #{ product.id }:{ ' ' }
                  <a href={ `/app/inbound-journey/products/${product.id}` }>{ product.sku || '-' }</a>
                  { ' ' }— { product.name || '-' }
                </div>
              )) }
              <Button
                color='success'
                size='sm'
                className='mt-2 mr-2'
                tag='a'
                href={ `/app/inbound-journey/tanda-masuk?user_id=${form.user_id || ''}&user_detail_id=${result.consignor_detail?.id || ''}&product_ids=${resultProductIds}` }
              >
                Create Tanda Masuk PDF
              </Button>
              <Button
                color='info'
                size='sm'
                className='mt-2'
                tag='a'
                href='/app/inbound-journey/inventory-batches'
              >
                Open Inventory Batch / Handover
              </Button>
            </div>
          ) }
        </CardBody>
      </Card>
    </>
  )
}

export default Phase3CreateIntakePage
